import { postAdRequest } from '../data/api'
import { fetchAndCacheBlob } from '../data/db/media-cache'
import { hideLoadingSpinner, showLoadingSpinner } from '../ui/display'
import { loadContent } from '../ui/handler'
import { getCurrentTimestamp } from '../utils/date'
import {
  getFirstQueuedItem,
  getLastQueuedItem,
  getListAdResponse,
  isQueueFull,
  pushQueue,
  removeFirstQueuedItem
} from '../data/local/queue'
import { parseVast } from '../utils/vast'

let fillingQueueIntervalId = null
let controller = null
let isRequestActive = false
let globalLogger = null

// Function to set global logger from index.js
export const setGlobalLoggerInPlayer = (logger) => {
  globalLogger = logger
}

export const initializePlayer = async () => {
  let firstQueuedItem = getFirstQueuedItem()
  if (!firstQueuedItem) {
    await initAdRequest()
    loadContent(getFirstQueuedItem())
    console.log('First ad loaded:', getFirstQueuedItem())
  } else {
    loadContent(firstQueuedItem)
    console.log('First ad loaded from existing queue:', firstQueuedItem)
  }
  startFillingQueue()
}

const initAdRequest = async () => {
  showLoadingSpinner()
  await requestAd()
  hideLoadingSpinner()
}

const startFillingQueue = () => {
  if (!fillingQueueIntervalId) {
    fillingQueueIntervalId = setInterval(async () => {
      if (isQueueFull()) {
        clearActiveRequests()
        return
      }

      if (isRequestActive) {
        return
      }

      await requestAd()
    }, 5000)
  }
}

const clearActiveRequests = () => {
  clearInterval(fillingQueueIntervalId)
  fillingQueueIntervalId = null
  if (controller) {
    controller.abort()
    controller = null
  }
  isRequestActive = false
}

const getNextTimeStamp = () => {
  const NEXT_TIMESTAMP_OFFSET = 15 // seconds or ms, match your units
  const MAX_AGE_SEC = 2 * 60 // 2 minutes
  const lastQueuedItem = getLastQueuedItem()
  const now = getCurrentTimestamp()
  // If the last cached item is recent (not older than MAX_AGE_SEC), use its timestamp for the next fetch.
  // Otherwise, use the current time to avoid requesting deprecated data.
  if (lastQueuedItem && now - lastQueuedItem.startTime < MAX_AGE_SEC) {
    console.log(
      'Using last queued item timestamp for next fetch:',
      lastQueuedItem.startTime + NEXT_TIMESTAMP_OFFSET
    )
    return lastQueuedItem.startTime + NEXT_TIMESTAMP_OFFSET
  }
  console.log('Using current time for next fetch:', now)
  return now
}

const isSameDataWithLastQueuedItem = (data) => {
  const lastQueuedItem = getLastQueuedItem()
  if (!lastQueuedItem) {
    return false
  }
  // Compare relevant fields
  return lastQueuedItem.startTime === data.startTime
}

export const loadNextAd = () => {
  removeFirstQueuedItem()
  startFillingQueue()
  const nextAd = getFirstQueuedItem()
  if (nextAd) {
    loadContent(nextAd)
    console.log('Next ad loaded:', nextAd)
  }
}

const fetchAndCacheBlobFirst = async (adData) => {
  let url = adData.url
  if (adData.type === 'vastTag') {
    url = parseVast(adData.url, true)
  }
  await fetchAndCacheBlob(url)
}

const requestAd = async () => {
  isRequestActive = true
  controller = new AbortController()

  if (globalLogger) {
    await globalLogger.addEvent('ad_request_start', {
      request_timestamp: Date.now(),
      request_type: 'ad_request',
      screen_uuid: new URLSearchParams(window.location.search).get('screenUuid')
    })
  }
  try {
    await postAdRequest({
      resolve: async (res) => {
        if (res?.data) {
          if (!isSameDataWithLastQueuedItem(res.data)) {
            pushQueue(res.data)
            await fetchAndCacheBlobFirst(res.data)
            console.log('Current ad list:', getListAdResponse())

            // Log successful content received
            if (globalLogger) {
              await globalLogger.addEvent('content_received', {
                content_type: res.data.type,
                content_url: res.data.url,
                adexchange_id: res.data.source?.adexchange_id,
                is_different: true,
                response_size: JSON.stringify(res.data).length
              })
            }
          }
          // Log postAdRequest SUCCESS
          if (globalLogger) {
            await globalLogger.addEvent('post_ad_request_success', {
              response_data_exists: !!res.data,
              content_type: res.data?.type,
              adexchange_id: res.data?.source?.adexchange_id,
              response_size_bytes: JSON.stringify(res).length,
              content_changed: isSameDataWithLastQueuedItem(res.data)
            })
          }
        }
      },
      signal: controller.signal,
      timestamp: getNextTimeStamp()
    })
  } catch (error) {
    // Log postAdRequest FAILURE
    if (globalLogger) {
      await globalLogger.addEvent('post_ad_request_failed', {
        error_message: error.message,
        error_stack: error.stack,
        error_type: error.constructor.name,
        screen_uuid: new URLSearchParams(window.location.search).get('screenUuid')
      })
    }

    console.error('Failed to request new content:', error)
  } finally {
    isRequestActive = false
  }
}
